/**
 * API client for the admin panel.
 *  - Access token lives only in memory (never localStorage) → an injected script can't steal a long-lived credential.
 *  - Refresh token is an HttpOnly cookie; refresh/logout send the double-submit CSRF header.
 *  - On 401 the client refreshes once (single-flight, shared by concurrent requests) and retries.
 */
export const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown, public requestId?: string) {
    super(message);
  }
  /** Field-level messages from validation errors: { "variants.0.sku": "..." } */
  get fieldErrors(): Record<string, string> {
    if (this.code !== 'VALIDATION_ERROR' || !Array.isArray(this.details)) return {};
    return Object.fromEntries((this.details as { path: string; message: string }[]).map((d) => [d.path, d.message]));
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onExpired: () => void = () => undefined;

export const session = {
  set(token: string | null) { accessToken = token; },
  get() { return accessToken; },
  onExpired(fn: () => void) { onExpired = fn; },
};

export function csrfToken(): string | undefined {
  return document.cookie.split('; ').find((c) => c.startsWith('csrf_token='))?.split('=')[1];
}

/** Single-flight refresh: N requests that hit 401 together trigger one refresh. */
export function refreshSession(fetchImpl: typeof fetch = fetch): Promise<boolean> {
  // No CSRF cookie ⇒ no session was ever issued in this browser (both cookies are set together): skip the request.
  if (!csrfToken()) { accessToken = null; return Promise.resolve(false); }
  refreshing ??= (async () => {
    try {
      const r = await fetchImpl(`${API_BASE}/admin/auth/refresh`, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json', 'x-csrf-token': csrfToken() ?? '' }, body: '{}' });
      if (!r.ok) { accessToken = null; return false; }
      accessToken = ((await r.json()) as { data: { accessToken: string } }).data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => { refreshing = null; }, 0);
    }
  })();
  return refreshing;
}

export type Query = Record<string, string | number | boolean | undefined | null>;
export interface RequestOptions { method?: string; body?: unknown; query?: Query; idempotencyKey?: string; signal?: AbortSignal }

export function buildUrl(path: string, query?: Query) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return `${API_BASE}${path}${s ? `?${s}` : ''}`;
}

async function send(path: string, o: RequestOptions, fetchImpl: typeof fetch): Promise<Response> {
  const headers: Record<string, string> = {};
  if (o.body !== undefined) headers['content-type'] = 'application/json';
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (o.idempotencyKey) headers['idempotency-key'] = o.idempotencyKey;
  if (path.startsWith('/admin/auth/')) headers['x-csrf-token'] = csrfToken() ?? '';
  return fetchImpl(buildUrl(path, o.query), { method: o.method ?? 'GET', headers, body: o.body === undefined ? undefined : JSON.stringify(o.body), credentials: 'include', signal: o.signal });
}

export async function raw(path: string, o: RequestOptions = {}, fetchImpl: typeof fetch = fetch): Promise<Response> {
  let res = await send(path, o, fetchImpl);
  if (res.status === 401 && !path.startsWith('/admin/auth/')) {
    if (await refreshSession(fetchImpl)) res = await send(path, o, fetchImpl);
    else onExpired();
  }
  if (!res.ok) {
    let body: { error?: { code?: string; message?: string; details?: unknown }; requestId?: string } = {};
    try { body = await res.json(); } catch { /* non-JSON error */ }
    throw new ApiError(res.status, body.error?.code ?? 'HTTP_ERROR', body.error?.message ?? `Request failed (${res.status})`, body.error?.details, body.requestId);
  }
  return res;
}

export async function api<T>(path: string, o: RequestOptions = {}, fetchImpl: typeof fetch = fetch): Promise<T> {
  const res = await raw(path, o, fetchImpl);
  if (res.status === 204) return undefined as T;
  return ((await res.json()) as { data: T }).data;
}

/** Authenticated file download (CSV exports). */
export async function download(path: string, query?: Query) {
  const res = await raw(path, { query });
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'export.csv';
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Opens an authenticated HTML document (invoice) in a new tab. */
export async function openHtml(path: string) {
  const win = window.open('', '_blank');
  const res = await raw(path);
  const url = URL.createObjectURL(new Blob([await res.text()], { type: 'text/html' }));
  if (win) win.location.href = url;
  else window.location.href = url;
}

export const newKey = () => crypto.randomUUID();
export const errorMessage = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Something went wrong');
